import { tx, txt, useContent } from "@/content/ContentContext";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Copy, Download, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { BrandMark } from "@/components/AppShell";
import { errMsg } from "@/lib/api";
import { formatMoney } from "@/lib/money";
import { BACKEND } from "@/lib/host";

const KEY = "samrtin_reseller_token";
const rApi = axios.create({ baseURL: `${BACKEND}/api/reseller` });
rApi.interceptors.request.use((c) => {
  const t = localStorage.getItem(KEY);
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});
const m = (n, dp, cur) => formatMoney(n, dp, cur);
const inp = "h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm outline-none focus:border-slate-900";

function Login({ onIn }) {
  const [f, setF] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await rApi.post("/login", f);
      localStorage.setItem(KEY, data.token);
      onIn(data.reseller);
    } catch (x) {
      setError(errMsg(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grain grid min-h-[100dvh] place-items-center bg-slate-950 px-4" data-testid="reseller-login-page">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-2xl bg-white p-6 shadow-2xl" data-testid="reseller-login-form">
        <BrandMark light={false} />
        <div>
          <h1 className="font-display text-2xl font-extrabold text-slate-900">{txt("reseller.reseller_login")}</h1>
          <p className="text-sm text-slate-500">{txt("reseller.separate_from_shop_owner_and")}</p>
        </div>
        <input type="email" className={inp} placeholder={tx("reseller.email")} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="reseller-login-email" />
        <input type="password" className={inp} placeholder={tx("reseller.password")} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="reseller-login-password" />
        {error && <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm font-semibold text-amber-800" data-testid="reseller-login-error">{error}</div>}
        <button disabled={busy || !f.email || !f.password} data-testid="reseller-login-submit" className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-slate-900 font-semibold text-white disabled:bg-slate-300">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Log in
        </button>
        <a href="/#reseller" className="block text-center text-sm font-semibold text-slate-600 underline decoration-amber-500 underline-offset-4" data-testid="reseller-login-apply-link">{txt("reseller.not_a_reseller_yet_apply")}</a>
      </form>
    </div>
  );
}

function Stat({ label, value, id }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 font-display text-2xl font-extrabold text-slate-900" data-testid={`reseller-stat-${id}`}>{value}</div>
    </div>
  );
}

function Money({ list, field = "amount", id }) {
  if (!list?.length) return <span data-testid={id}>—</span>;
  return <span className="flex flex-col" data-testid={id}>{list.map((x) => <span key={x.currency}>{m(x[field], x.decimals, x.currency)}</span>)}</span>;
}

function Performance({ p }) {
  const link = `${window.location.origin}/signup?ref=${p.code}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); } catch { /* clipboard blocked: user can select the text */ }
    toast.success(tx("reseller.referral_link_copied"));
  };
  return (
    <div className="space-y-6" data-testid="reseller-performance">
      <div className="rounded-2xl bg-slate-950 p-5 text-white">
        <div className="text-xs font-bold uppercase tracking-wider text-amber-400">{txt("reseller.your_referral_link")}</div>
        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
          <code className="flex-1 truncate rounded-xl bg-white/10 px-3 py-3 font-mono text-sm" data-testid="reseller-link">{link}</code>
          <button onClick={copy} data-testid="reseller-copy-link" className="flex h-11 items-center justify-center gap-2 rounded-xl bg-amber-500 px-4 font-bold text-slate-950 hover:bg-amber-400"><Copy className="h-4 w-4" /> {txt("reseller.copy")}</button>
        </div>
        <div className="mt-3 text-sm text-slate-400">{txt("reseller.code")} <b className="font-mono text-white" data-testid="reseller-code">{p.code}</b> · {p.rate}% · {p.mode === "first" ? "First payment only" : tx("reseller.recurring")}</div>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label={tx("reseller.referred")} value={p.referred} id="referred" />
        <Stat label={tx("reseller.in_demo")} value={p.demo} id="demo" />
        <Stat label={tx("reseller.paying")} value={p.paying} id="paying" />
        <Stat label={tx("reseller.expired")} value={p.expired} id="expired" />
        <Stat label={tx("reseller.conversion")} value={`${p.conversion}%`} id="conversion" />
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <Stat label={tx("reseller.earned_this_month")} value={<Money list={p.this_month} id="reseller-earned-month" />} id="month" />
        <Stat label={tx("reseller.earned_all_time")} value={<Money list={p.balances} field="earned" id="reseller-earned-all" />} id="all" />
        <Stat label={tx("reseller.balance")} value={<Money list={p.balances} field="balance" id="reseller-balance" />} id="balance" />
      </div>
    </div>
  );
}

const BADGE = { Demo: "bg-sky-100 text-sky-800", Paying: "bg-emerald-100 text-emerald-800", Expired: "bg-slate-200 text-slate-700" };

function Businesses() {
  const [items, setItems] = useState(null);
  useEffect(() => { rApi.get("/businesses").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  if (!items) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  if (!items.length) return <p className="text-sm text-slate-500" data-testid="reseller-businesses-empty">{txt("reseller.no_referred_businesses_yet_share")}</p>;
  return (
    <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white" data-testid="reseller-businesses">
      {items.map((b) => (
        <div key={b.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3" data-testid={`reseller-business-${b.id}`}>
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold text-slate-900">{b.name}</div>
            <div className="text-xs text-slate-500">Signed up {b.signup_date} · Plan {b.plan} · Last payment {b.last_payment || "—"}</div>
          </div>
          <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${BADGE[b.status]}`} data-testid={`reseller-business-status-${b.id}`}>{b.status}</span>
        </div>
      ))}
    </div>
  );
}

async function download(format, range) {
  const r = await rApi.get("/statement/export", { params: { format, ...range }, responseType: "blob" });
  const name = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "")?.[1] || `statement.${format}`;
  const url = URL.createObjectURL(r.data);
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function Statement() {
  const [range, setRange] = useState({ date_from: "", date_to: "" });
  const [data, setData] = useState(null);
  useEffect(() => { rApi.get("/statement", { params: range }).then((r) => setData(r.data)).catch(() => {}); }, [range]);
  const dl = (f) => download(f, range).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-4" data-testid="reseller-statement">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs font-bold uppercase tracking-wider text-slate-600">{txt("reseller.from")}<input type="date" className={`${inp} mt-1 w-40`} value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} data-testid="reseller-statement-from" /></label>
        <label className="flex flex-col text-xs font-bold uppercase tracking-wider text-slate-600">{txt("reseller.to")}<input type="date" className={`${inp} mt-1 w-40`} value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} data-testid="reseller-statement-to" /></label>
        <div className="ml-auto flex gap-2">
          <button onClick={() => dl("pdf")} data-testid="reseller-download-pdf" className="flex h-11 items-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white"><Download className="h-4 w-4" /> {txt("reseller.download_statement_pdf")}</button>
          <button onClick={() => dl("csv")} data-testid="reseller-download-csv" className="flex h-11 items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-900"><Download className="h-4 w-4" /> {txt("reseller.csv")}</button>
        </div>
      </div>
      {data && (
        <>
          <div className="flex flex-wrap gap-3" data-testid="reseller-statement-totals">
            {data.balances.map((b) => (
              <div key={b.currency} className="rounded-xl bg-slate-100 px-4 py-2 font-mono text-xs text-slate-700" data-testid={`reseller-total-${b.currency}`}>
                {b.currency}: earned {m(b.earned, b.decimals)} · paid {m(b.paid, b.decimals)} · <b>balance {m(b.balance, b.decimals)}</b>
              </div>
            ))}
          </div>
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-3 py-2">{txt("reseller.date")}</th><th className="px-3 py-2">{txt("reseller.details")}</th><th className="px-3 py-2 text-right">{txt("reseller.earned")}</th><th className="px-3 py-2 text-right">{txt("reseller.paid")}</th><th className="px-3 py-2 text-right">{txt("reseller.balance")}</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.rows.map((e) => (
                  <tr key={e.id} data-testid={`reseller-statement-row-${e.id}`}>
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{e.date}</td>
                    <td className="px-3 py-2">{e.type === "earned" ? <><b>{e.business}</b> · {e.plan} {e.period} · payment {m(e.payment, e.decimals, e.currency)} @ {e.rate}%</> : <><b>{txt("reseller.payout")}</b> {[e.method, e.reference].filter(Boolean).join(" · ")}</>}</td>
                    <td className="px-3 py-2 text-right font-mono">{e.delta > 0 ? m(e.delta, e.decimals, e.currency) : ""}</td>
                    <td className="px-3 py-2 text-right font-mono">{e.delta < 0 ? m(-e.delta, e.decimals, e.currency) : ""}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">{m(e.balance, e.decimals, e.currency)}</td>
                  </tr>
                ))}
                {!data.rows.length && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500" data-testid="reseller-statement-empty">{txt("reseller.no_entries_in_this_range")}</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const TABS = () => ([["performance", tx("reseller.performance")], ["businesses", tx("reseller.businesses")], ["statement", tx("reseller.statement")]]);

export default function Reseller() {
  useContent();
  const [me, setMe] = useState(undefined);
  const [perf, setPerf] = useState(null);
  const [tab, setTab] = useState("performance");
  const load = useCallback(() => {
    if (!localStorage.getItem(KEY)) return setMe(null);
    rApi.get("/performance").then((r) => { setPerf(r.data); setMe(true); }).catch(() => { localStorage.removeItem(KEY); setMe(null); });
  }, []);
  useEffect(load, [load]);
  if (me === undefined) return <div className="min-h-[100dvh] bg-slate-950" />;
  if (!me) return <Login onIn={load} />;
  return (
    <div className="min-h-[100dvh] bg-slate-50" data-testid="reseller-portal">
      <header className="grain sticky top-0 z-30 bg-slate-950">
        <div className="mx-auto flex h-14 max-w-5xl items-center gap-3 px-4">
          <BrandMark />
          <span className="rounded-full bg-amber-500/15 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-amber-400">{txt("reseller.reseller")}</span>
          <button onClick={() => { localStorage.removeItem(KEY); setMe(null); }} data-testid="reseller-logout" aria-label={tx("reseller.log_out")} className="ml-auto grid h-10 w-10 place-items-center rounded-lg text-slate-400 hover:bg-white/10 hover:text-white"><LogOut className="h-4 w-4" /></button>
        </div>
        <nav className="mx-auto flex max-w-5xl gap-1 overflow-x-auto px-4">
          {TABS().map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`reseller-tab-${k}`} className={`h-11 shrink-0 border-b-2 px-3 text-sm font-semibold ${tab === k ? "border-amber-500 text-white" : "border-transparent text-slate-400 hover:text-white"}`}>{l}</button>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-6 sm:py-8">
        {perf?.status === "inactive" && <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800" data-testid="reseller-inactive-banner">Your reseller account is deactivated. New sign-ups and payments no longer earn commission; your statement is kept.</div>}
        {tab === "performance" && perf && <Performance p={perf} />}
        {tab === "businesses" && <Businesses />}
        {tab === "statement" && <Statement />}
      </main>
    </div>
  );
}
